import { DialogComponent, DialogOpenEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
  }

  #handleCartAdd = () => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }
  };

  open() {
    this.showDialog();

    // Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent =
        document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries, so query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute(
      'cart-summary-sticky',
      ratio > this.#summaryThreshold ? 'false' : 'true'
    );
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}

/* ============================================================================
   TeeInBlue -> Horizon Drawer FIX (ACTUAL + SIMPLE)
   Change requested:
   ✅ Drawer opens 1 second later on Add-to-cart (no empty flash)
   ✅ View cart opens immediately (NO WAIT)
============================================================================ */

let teeBusy = false;
let teeOpenTimer = null;
const teeSleep = (ms) => new Promise((r) => setTimeout(r, ms));

function teeClearOpenTimer() {
  if (teeOpenTimer) {
    clearTimeout(teeOpenTimer);
    teeOpenTimer = null;
  }
}

function teeOpenDrawerDelayed(ms = 1000) {
  teeClearOpenTimer();
  teeOpenTimer = setTimeout(() => {
    const drawer = document.querySelector('cart-drawer-component');
    if (drawer && typeof drawer.open === 'function') drawer.open();
  }, ms);
}

/**
 * Find the REAL section handle that contains the drawer (header section)
 * Example: shopify-section-sections--2691876...__header_section
 * -> sections--2691876...__header_section
 */
function teeGetDrawerSectionKeyFromDOM() {
  const drawer = document.querySelector('cart-drawer-component');
  const wrap = drawer?.closest?.('[id^="shopify-section-"]');
  if (!wrap?.id) return null;
  return wrap.id.replace('shopify-section-', '');
}

async function teeFetchCart() {
  const r = await fetch('/cart.js?_ts=' + Date.now(), {
    credentials: 'same-origin',
    cache: 'no-store',
    headers: { Accept: 'application/json' },
  });
  return r.json();
}

async function teeWaitCartChange(prevCount, timeoutMs = 7000) {
  const start = Date.now();
  while (Date.now() - start < timeoutMs) {
    const cart = await teeFetchCart();
    const c = cart?.item_count ?? 0;
    if (c !== prevCount) return cart;
    await teeSleep(120);
  }
  return teeFetchCart();
}

/**
 * Fetch HTML for the REAL header section that contains the drawer.
 * Tries / first (most reliable), then /cart as fallback.
 */
async function teeFetchHeaderSectionHTML() {
  const key = teeGetDrawerSectionKeyFromDOM();
  if (!key) {
    console.warn('[TEE] Could not detect drawer section key from DOM');
    return null;
  }

  const tryUrls = [
    new URL('/', window.location.origin),
    new URL('/cart', window.location.origin),
  ];

  for (const u of tryUrls) {
    u.searchParams.set('sections', key);
    u.searchParams.set('_ts', Date.now().toString());

    const r = await fetch(u.toString(), {
      method: 'GET',
      credentials: 'same-origin',
      cache: 'no-store',
      headers: { Accept: 'application/json' },
    });

    let data;
    try {
      data = await r.json();
    } catch (e) {
      continue;
    }

    const html = data?.[key];
    if (typeof html !== 'string' || !html.includes('cart-drawer-component')) {
      continue;
    }

    return html;
  }

  return null;
}

/**
 * Replace ONLY drawer + bubble from incoming header HTML
 */
function teePatchDrawerAndBubbleFromHeaderHTML(headerHtml) {
  const parser = new DOMParser();
  const doc = parser.parseFromString(headerHtml, 'text/html');

  // drawer component swap
  const incomingDrawer = doc.querySelector('cart-drawer-component');
  const currentDrawer = document.querySelector('cart-drawer-component');
  if (incomingDrawer && currentDrawer) currentDrawer.replaceWith(incomingDrawer);

  // bubble swap (badge)
  const incomingBubble = doc.querySelector('#cart-icon-bubble');
  const currentBubble = document.querySelector('#cart-icon-bubble');
  if (incomingBubble && currentBubble) currentBubble.replaceWith(incomingBubble);
}

async function teeFullRefresh() {
  if (teeBusy) return;
  teeBusy = true;

  try {
    // 1) baseline
    const before = await teeFetchCart();
    const beforeCount = before?.item_count ?? 0;

    // 2) wait until Tee actually updated cart
    await teeWaitCartChange(beforeCount);

    // 3) fetch real header section HTML
    const headerHtml = await teeFetchHeaderSectionHTML();
    if (!headerHtml) {
      console.warn('[TEE] Could not fetch header section HTML');
      return;
    }

    // 4) patch drawer + bubble
    teePatchDrawerAndBubbleFromHeaderHTML(headerHtml);

    // 5) open drawer 1s later (so it opens already filled)
    teeOpenDrawerDelayed(1000);
  } catch (e) {
    console.warn('[TEE] teeFullRefresh failed:', e);
  } finally {
    teeBusy = false;
  }
}

/**
 * View cart should NOT wait for cart change.
 * It should open immediately with current cart state.
 */
async function teeRefreshDrawerNoWait() {
  if (teeBusy) return;
  teeBusy = true;

  try {
    const headerHtml = await teeFetchHeaderSectionHTML();
    if (!headerHtml) {
      console.warn('[TEE] Could not fetch header section HTML (view cart)');
      return;
    }

    teePatchDrawerAndBubbleFromHeaderHTML(headerHtml);

    // View cart => open NOW
    teeOpenDrawerDelayed(0);
  } catch (e) {
    console.warn('[TEE] teeRefreshDrawerNoWait failed:', e);
  } finally {
    teeBusy = false;
  }
}

/* ===== Triggers ===== */

/**
 * A) TeeInBlue ATC button click
 */
document.addEventListener(
  'click',
  (e) => {
    const btn = e.target?.closest?.('#teeAtcButton');
    if (!btn) return;
    teeFullRefresh();
  },
  true
);

/**
 * B) TeeInBlue "View cart" -> open immediately (NO WAIT)
 */
document.addEventListener(
  'click',
  (e) => {
    const el = e.target?.closest?.('a,button');
    if (!el) return;

    const href = (el.getAttribute?.('href') || '').toLowerCase();
    const txt = (el.textContent || '').trim().toLowerCase();

    const isViewCart =
      href === '/cart' ||
      href.endsWith('/cart') ||
      txt === 'view cart' ||
      txt.includes('view cart') || // e.g. "View cart (1)"
      txt === 'warenkorb ansehen';

    if (!isViewCart) return;

    e.preventDefault();
    e.stopPropagation();
    teeRefreshDrawerNoWait();
  },
  true
);

/**
 * C) Extra: if TeeInBlue adds via fetch/XHR to /cart/add.js
 */
(function hookCartAddCompletion() {
  const originalFetch = window.fetch?.bind(window);
  if (originalFetch) {
    window.fetch = async (...args) => {
      const res = await originalFetch(...args);
      try {
        const url = typeof args[0] === 'string' ? args[0] : args[0]?.url;
        if (url && (url.includes('/cart/add.js') || url.includes('/cart/add'))) {
          setTimeout(() => teeFullRefresh(), 0);
        }
      } catch (_) {}
      return res;
    };
  }

  const origOpen = XMLHttpRequest.prototype.open;
  const origSend = XMLHttpRequest.prototype.send;

  XMLHttpRequest.prototype.open = function (method, url, ...rest) {
    this.__tee_url = url;
    return origOpen.call(this, method, url, ...rest);
  };

  XMLHttpRequest.prototype.send = function (...args) {
    this.addEventListener('load', () => {
      try {
        const url = this.__tee_url || '';
        if (url.includes('/cart/add.js') || url.includes('/cart/add')) {
          setTimeout(() => teeFullRefresh(), 0);
        }
      } catch (_) {}
    });
    return origSend.call(this, ...args);
  };
})();